import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Select from "src/components/common/gridWrapper/Grid_Select";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { PUR2020_R01Dto, defaultPUR2020_R01Data, GridRowData } from "./Dto_PUR2020_R01";
import { usePUR2020_R01Store } from "./Store_PUR2020_R01";
import { formatDate } from "src/utils/string/stringUtil";
import { CommonCodeColumn } from "src/utils/grid/GridColumnContent";

import { formatNumber } from "src/utils/number/numberUtils";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import { cellDateComparator } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = 'PUR2020_R01-grid-column-state';

interface Props {
    gridRef: React.RefObject<AgGridReact<PUR2020_R01Dto> | null>;
}

export const Table_PUR2020_R01 = (props: Props) => {

    const { gridRef } = props;

    const { 
        PUR2020_R01List,
        selectedPUR2020_R01List,  setSelectedPUR2020_R01List, addSelectedPUR2020_R01List, removeSelectedPUR2020_R01List, initSelectedPUR2020_R01List,
        
        columnState, setColumnState,
        scrollTop, setScrollTop,
    } = usePUR2020_R01Store();

    const { 
        jobTeamList,
        ordTypeList,
        unitList,
        compCdList, 
        employeeCdList,
        workPlaceList,
    } = useCommonCodeStore();

    const codeMaps = useMemo(() => {
        return {
            ordType: new Map(ordTypeList.map(v => [v.minorCd, v.codeNm])),
            workPlace: new Map(workPlaceList.map(v => [v.workplaceCd, v.workplaceNm])),
            unit: new Map(unitList.map(v => [v.minorCd, v.codeNm])),
            emp: new Map(employeeCdList.map(v => [v.empNo, v.empNm])),
            comp: new Map(compCdList.map(v => [v.compCd, v.compNm])),
        };
    }, [ordTypeList, workPlaceList, unitList, employeeCdList, compCdList]);

    // 복합키 생성 함수
    const getCompositeKey = useCallback((row: GridRowData) => 
        `${row.transportNo}|${row.itemCd}|${row.customCd}|${row.warehouseCd}`,
    []);

    const {
        createRowClassRules,
        settingRowIndex,
        ensureSelectedRow,
    } = useGridSettingContent({
        gridRef,
        key: 'valueIndex',
        gridData: PUR2020_R01List || [],
        defaultGridData: defaultPUR2020_R01Data as GridRowData,
        selectedRow: selectedPUR2020_R01List,
    });
    const getRowIdForGrid = useCallback((params: any) => {
        const id = params.data?.valueIndex || params.id;
        return String(id);
    }, []);
    const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

    const headerNames: Record<string, string> = {
        rowIndex: '',
        compNm: '사업장명',
        logicCenterNm: '물류센터',
        warehouseNm: '창고코드',
        customCd: '거래처',
        customNm: '상호',
        transportDt: '입고일자',
        inoutClassNm: '입고구분',
        itemCd: '품목코드',
        itemNm: '품명',
        itemSpec: '규격',
        locationNm: '보관위치',
        itemStatus: '품목등급',
        unit: '단위',
        qty: '수불량',
        cost: '수불원가',
        amt: '금액',
        
    };

    const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'compCd')(params), []);

    const baseColumnDefs: ColDef<PUR2020_R01Dto>[] = useMemo(() => [
        ...getDefaultRowIndexColumnConfig(),
        {
            headerName: headerNames.compNm, colId: 'compNm', field: 'compNm', width: 120,
            editable: false,
            cellStyle: (p) => getCellStyle(p, 'compNm'),
        },
        {
            headerName: headerNames.logicCenterNm, colId: 'logicCenterNm', field: 'logicCenterNm', width: 120,
            editable: false,
            cellStyle: (p) => getCellStyle(p, 'logicCenterNm'),
        },
        {
            headerName: headerNames.warehouseNm, colId: 'warehouseNm', field: 'warehouseNm', width: 120,
            editable: false,
            cellStyle: (p) => getCellStyle(p, 'warehouseNm'),
        },
        {
            headerName: headerNames.customCd, colId: 'customCd', field: 'customCd', width: 110,
            editable: false,
            cellStyle: (p) => getCellStyle(p, 'customCd'),
        },
        {
            headerName: headerNames.customNm, colId: 'customNm', field: 'customNm', width: 140,
            editable: false,
            cellStyle: (p) => getCellStyle(p, 'customNm'),
        },
        {
            headerName: headerNames.transportDt, colId: 'transportDt', field: 'transportDt', width: 120,
            editable: false,
            cellStyle: (p) => getCellStyle(p, 'transportDt'),
            valueFormatter: (p: { data?: PUR2020_R01Dto }) => {
                if (p.data?.transportDt) {
                    const date = new Date(p.data.transportDt);
                    if (!isNaN(date.getTime())) return formatDate(date);
                }
                return '';
            },
            comparator: cellDateComparator,
        },
        {
            headerName: headerNames.inoutClassNm, colId: 'inoutClassNm', field: 'inoutClassNm', width: 100,
            editable: false,
            cellStyle: (p) => getCellStyle(p, 'inoutClassNm'),
        },
        {
            headerName: headerNames.itemCd, colId: 'itemCd', field: 'itemCd', width: 110,
            editable: false,
            cellStyle: (p) => getCellStyle(p, 'itemCd'),
        },
        {
            headerName: headerNames.itemNm, colId: 'itemNm', field: 'itemNm', width: 200,
            editable: false,
            cellStyle: (p) => getCellStyle(p, 'itemNm'),
        },
        {
            headerName: headerNames.itemSpec, colId: 'itemSpec', field: 'itemSpec', width: 200,
            editable: false,
            cellStyle: (p) => getCellStyle(p, 'itemSpec'),
        },
        {
            headerName: '보관위치', colId: 'locationNm', field: 'locationNm', width: 120,
            editable: false,
            cellStyle: (p) => getCellStyle(p, 'locationNm'),
        },
        {
            headerName: headerNames.itemStatus, colId: 'itemStatus', field: 'itemStatus', width: 90,
            editable: false,
            cellStyle: (p) => getCellStyle(p, 'itemStatus'),
        },
        {
            headerName: headerNames.unit, colId: 'unit', field: 'unit', width: 80,
            cellStyle: (p) => getCellStyle(p, 'unit'),
            ...CommonCodeColumn.options(unitList, false, undefined, undefined),
        },
        {
            headerName: headerNames.qty, colId: 'qty', field: 'qty', width: 90,
            editable: false,
            type: 'numericColumn',
            headerClass: aggridHeaderClass,
            cellStyle: (p) => getCellStyle(p, 'qty'),
            valueFormatter: (p) => formatNumber(p.value || 0),
        },
        {
            headerName: headerNames.cost, colId: 'cost', field: 'cost', width: 90,
            editable: false,
            type: 'numericColumn',
            headerClass: aggridHeaderClass,
            cellStyle: (p) => getCellStyle(p, 'cost'),
            valueFormatter: (p) => formatNumber(p.value || 0),
        },
        {
            headerName: headerNames.amt, colId: 'amt', field: 'amt', width: 110,
            editable: false,
            type: 'numericColumn',
            headerClass: aggridHeaderClass,
            cellStyle: (p) => getCellStyle(p, 'amt'),
            valueFormatter: (p) => formatNumber(p.value || 0),
        },
    ], [getCellStyle, cellDateComparator, unitList, headerNames]);

    const defaultColumnState = useMemo(
        () => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
    , [baseColumnDefs]);

    const columnDefs: ColDef<GridRowData>[] = useMemo(() => {
        if (columnState === null) {
            return baseColumnDefs;
        }
        return GridCustomColumnDefs<PUR2020_R01Dto>({
            baseColumnDefs,
            columnState,
            headerNames,
            fallbackHeaderClass: aggridHeaderClass,
        });
    }, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

    const getRowStyleForPUR2020 = useCallback((params: any) => {
        if (params.node.rowPinned === 'bottom') {
            return { background: '#F0F0F0', fontWeight: 'bold', borderTop: 'none' };
        }
        return undefined;
    }, []);

    const shiftRangeSelectionConfig = useMemo(() => ({
        enableShiftRangeSelect: true,
        selectedIds: selectedPUR2020_R01List as Array<string | number>,
        setSelectedIds: (ids: Array<string | number>) => setSelectedPUR2020_R01List(ids.map(String)),
        resolveId: (row: GridRowData) => getCompositeKey(row),
    }), [selectedPUR2020_R01List, setSelectedPUR2020_R01List, getCompositeKey]);

    const pinnedBottomRow = useMemo(() => {
        const detailRows = PUR2020_R01List;
        const totals = detailRows.reduce((acc, item) => {
            return {
                qty: acc.qty + (Number(item.qty) || 0),
                cost: acc.cost + (Number(item.cost) || 0),
                amt: acc.amt + (Number(item.amt) || 0),
                editable: false,
            };
        }, { qty: 0, cost: 0, amt: 0, editable: false });

        return [{
            rowIndex: '',
            compCd: detailRows.length.toLocaleString() + "건",
            qty: totals.qty,
            cost: totals.cost,
            amt: totals.amt,
            editable: false,
        }];
    }, [PUR2020_R01List]);

    useEffect(() => { ensureSelectedRow(); }, [PUR2020_R01List, selectedPUR2020_R01List]);

    // 복합키 기반 rowClassRules
    const rowClassRules = useMemo(() => ({
        'selected': (params: any) => {
            if (!params.data) return false;
            const compositeKey = getCompositeKey(params.data);
            return selectedPUR2020_R01List.includes(compositeKey);
        }
    }), [selectedPUR2020_R01List, getCompositeKey]);

    const handleRowDragEnd = useCallback((e: any) => {
        const api = gridRef.current?.api || e?.api;
        if (!api) return;
        
        if (api.applyColumnState) {
            api.applyColumnState({ defaultState: { sort: null as any, sortIndex: null as any } });
        }
        
        const reordered: GridRowData[] = [];
        api.forEachNodeAfterFilterAndSort((node: any, idx: number) => {
            if (!node?.data) return;
            reordered.push({
                ...node.data,
                orderIndex: idx + 1,
                orderIndexSeq: 0,
            } as GridRowData);
        });
        // 드래그 후 리스트 자체는 유지 (필요시 상태 업데이트 로직 추가)
    }, [gridRef]);

    return (
        <div className="flex flex-col gap-3 h-full min-h-0">
            <GridContextWrapper<GridRowData>
            	enableCellCalculation
                gridRef={gridRef}
                paddedGridData={PUR2020_R01List}
                initSelected={initSelectedPUR2020_R01List}
				defaultRowData={defaultPUR2020_R01Data}
                baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
                defaultColumnState={defaultColumnState}
                setColumnState={setColumnState}
                localStorageKey={COLUMN_STATE_KEY}
                menuItems={undefined}
            >
                <Grid_Select<GridRowData>
                    ref={gridRef}
                    rowData={PUR2020_R01List}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    getRowId={getRowIdForGrid}
                    onCellKeyDown={undefined}
					setHoveredRowIndex={setHoveredRowIndex}
					columnStateKey={COLUMN_STATE_KEY}
					setColumnState={setColumnState}
                    onGridReady={(params) => {
                        params.api.getDisplayedRowCount();
                        params.api.getRenderedNodes();
                    }}
                    shiftRangeSelection={shiftRangeSelectionConfig}
                    pinnedBottomRowData={pinnedBottomRow}
                    rowClassRules={rowClassRules}
                    getRowStyle={getRowStyleForPUR2020}
                    rowDragManaged={true}
                    rowDragEntireRow={true}
                    onRowDragEnd={handleRowDragEnd}
                    isRowSelectable={(node) => !!node.data}
                    suppressRowClickSelection={false}
                    ensureDomOrder={true}
                    scrollTop={scrollTop}
                    setScrollTop={setScrollTop}
                />
            </GridContextWrapper>
        </div>
    );
}


